import { Form } from 'antd'
import { errorMessage, successMessage } from 'components/BannerComponent'
import { useStoreActions, useStoreState } from 'easy-peasy'
import jwtDecode from 'jwt-decode'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import {
  pushProfile,
  requestNotificationPermission
} from 'utils/clevertap_service'
import { AUTH_FLAGS, GENERIC_FAIL_MESSAGE, SC_ORIGIN } from 'utils/constants'
import { serviceWorker } from 'utils/sw'

export default function Container({ children }) {
  const [form] = Form.useForm()
  const router = useRouter()

  const {
    loginStateV2,
    convertedGoogleToken,
    convertedFacebookToken,
    me,
    accSharingModalVisible,
    socialMediaToVerify,
    latestActiveSc,
    verifyEmailState,
    forceResetOtpState,
    forceUpdatePasswordState
  } = useStoreState((s) => ({
    loginStateV2: s.loginStateV2,
    convertedGoogleToken: s.convertedGoogleToken,
    convertedFacebookToken: s.convertedFacebookToken,
    me: s.me,
    // account data sharing states
    accSharingModalVisible: s.accSharingModalVisible,
    logoutState: s.logoutState,
    socialMediaToVerify: s.socialMediaToVerify,
    latestActiveSc: s.latestActiveSc,
    verifyEmailState: s.verifyEmailState,
    forceResetOtpState: s.forceResetOtpState,
    forceUpdatePasswordState: s.forceUpdatePasswordState
  }))

  const {
    setGoogleConvert,
    convertGoogleToken,
    setFacebookConvert,
    convertFacebookToken,
    setMe,
    setSocialMediaToVerify,
    getLatestActiveSc,
    // accoutn sharing actions
    setAccSharingModalVisible,
    loginV2,
    setLoginStateV2,
    getScCardsV2,
    verifyEmail,
    setVerifyEmailState,
    forceResetOtp,
    setForceResetOtpState,
    setForceUpdatePasswordState,
    logout
  } = useStoreActions((a) => ({
    setGoogleConvert: a.setGoogleConvert,
    convertGoogleToken: a.convertGoogleToken,
    setFacebookConvert: a.setFacebookConvert,
    convertFacebookToken: a.convertFacebookToken,
    setMe: a.setMe,
    setSocialMediaToVerify: a.setSocialMediaToVerify,
    // get latest active supercard
    getLatestActiveSc: a.getLatestActiveSc,
    // account sharing actions
    setAccSharingModalVisible: a.setAccSharingModalVisible,
    // login v2
    loginV2: a.loginV2,
    setLoginStateV2: a.setLoginStateV2,
    getScCardsV2: a.getScCardsV2,
    verifyEmail: a.verifyEmail,
    setVerifyEmailState: a.setVerifyEmailState,
    forceResetOtp: a.forceResetOtp,
    setForceResetOtpState: a.setForceResetOtpState,
    setForceUpdatePasswordState: a.setForceUpdatePasswordState,
    logout: a.logout
  }))

  const [socialLogin, setSocialLogin] = useState(null)
  const [passwordStatus, setPasswordStatus] = useState('verify')
  const [accountInactive, setAccountInactive] = useState(false)
  const [confirmOtp, setConfirmOtp] = useState(null)
  const [passwordUpdatedModalVisible, setPasswordUpdatedModalVisible] =
    useState(false)
  const [redirectLoginDetails, setRedirectLoginDetails] = useState(null)

  const checkIfPasswordIsUpdated = (value) => {
    const { username } = value
    verifyEmail({ email: username })
  }

  const cancelCurrentLogin = () => {
    sessionStorage.removeItem('loginEmail')
    form.resetFields()
    setPasswordStatus('verify')
    sessionStorage.removeItem('creds')
  }

  const handleLogin = (value) => {
    const { username, password } = value

    loginV2({ username, password: password })
    sessionStorage.setItem('creds', JSON.stringify({ username, password }))
  }

  const handleLoginFacebook = (response, allowAccess = false) => {
    if (typeof response.accessToken !== 'undefined') {
      setSocialLogin({ type: 'facebook', response })
      localStorage.setItem('img', response?.picture?.data?.url)
      convertFacebookToken({
        token: response.accessToken,
        allow_platform_access: allowAccess
      })
    } else {
      localStorage.setItem('img', '')
      errorMessage('Login failed.')
      console.log('fired facebook?')
    }
  }

  const handleLoginGoogle = (response, allowAccess = false) => {
    if (typeof response.accessToken !== 'undefined') {
      setSocialLogin({ type: 'google', response })
      localStorage.setItem('img', response?.picture?.data?.url)
      convertGoogleToken({
        token: response?.accessToken,
        allow_platform_access: allowAccess
      })
    } else {
      localStorage.setItem('img', '')
      errorMessage('Login failed.')
      console.log('failed google login', response)
    }
  }

  const agreeToShare = () => {
    const socialAuth = JSON.parse(sessionStorage.getItem('socialLogin'))
    if (socialAuth && typeof socialAuth === 'object') {
      const { type, response } = socialAuth

      if (type === 'facebook') {
        handleLoginFacebook(response, true)
      } else {
        handleLoginGoogle(response, true)
      }
    } else {
      const creds = JSON.parse(sessionStorage.getItem('creds'))

      loginV2({
        username: creds?.username,
        password: creds?.password,
        allow_platform_access: true
      })
    }
    setAccSharingModalVisible(false)
  }

  const handleClosePasswordUpdatedModal = () => {
    setPasswordUpdatedModalVisible(false)
    if (redirectLoginDetails) {
      redirectLogin(redirectLoginDetails)
    }
  }

  useEffect(() => {
    setAccSharingModalVisible(false)
    setPasswordStatus('verify')

    return () => {
      const initialState = { status: 0, payload: null }

      setLoginStateV2(initialState)
      setGoogleConvert(initialState)
      setFacebookConvert(initialState)
      setMe(initialState)
      setForceResetOtpState(initialState)
      setAccSharingModalVisible(false)

      sessionStorage.removeItem('creds')

      setPasswordStatus('verify')
    }
  }, [])

  useEffect(() => {
    const { status } = forceUpdatePasswordState
    if (status === 2) {
      // Use a ref to prevent duplicate calls
      const currentUsername = form.getFieldValue('username')
      const currentPassword = form.getFieldValue('password')

      // Clean up the state to prevent triggering again
      setForceUpdatePasswordState({ status: 0, payload: null })

      setPasswordUpdatedModalVisible(true)

      // Then handle the login
      handleLogin({
        username: currentUsername,
        password: currentPassword
      })
    }
  }, [forceUpdatePasswordState])

  useEffect(() => {
    const { status, payload } = verifyEmailState
    if (status === 2) {
      if (!payload.is_password_updated) {
        forceResetOtp({ email: form.getFieldValue('username') })
        setVerifyEmailState({ status: 0, payload: null })
      } else {
        setPasswordStatus('login')
      }

      successMessage(payload.message || 'OTP sent to your email')
    } else if (status === 3) {
      if (payload?.code === AUTH_FLAGS.ACCOUNT_INACTIVE) {
        setAccountInactive(true)
      } else {
        errorMessage(payload.details || GENERIC_FAIL_MESSAGE)
      }

      setVerifyEmailState({ status: 0, payload: null })
    }
  }, [verifyEmailState])

  useEffect(() => {
    const { status, payload } = forceResetOtpState

    if (status === 2) {
      setPasswordStatus('otp')
    } else if (status === 3) {
      errorMessage(payload.details || GENERIC_FAIL_MESSAGE)
      setForceResetOtpState({ status: 0, payload: null })
    }
  }, [forceResetOtpState])

  useEffect(() => {
    if (socialLogin) {
      sessionStorage.setItem('socialLogin', JSON.stringify(socialLogin))
    }
  }, [socialLogin])

  useEffect(() => {
    const { status, payload } = loginStateV2

    if (status === 2) {
      if (
        typeof payload.code !== 'undefined' &&
        payload.code === AUTH_FLAGS.ACCOUNT_PERMISSION_REQUIRED
      ) {
        setAccSharingModalVisible(true)
      } else if (
        typeof payload.code !== 'undefined' &&
        payload.code === AUTH_FLAGS.INVALID_CREDENTIALS
      ) {
        errorMessage(payload.details[0] || GENERIC_FAIL_MESSAGE)
        setLoginStateV2({ status: 0, payload: null })
      } else {
        const details = jwtDecode(payload.access_token)

        if (!passwordUpdatedModalVisible) {
          redirectLogin(details)
        }
        setRedirectLoginDetails(details)

        setLoginStateV2({ status: 0, payload: null })
      }
    } else if (status === 3) {
      errorMessage(payload.message || GENERIC_FAIL_MESSAGE)
      setLoginStateV2({ status: 0, payload: null })
    }
  }, [loginStateV2, passwordUpdatedModalVisible])

  useEffect(() => {
    const { status, payload } = convertedGoogleToken
    if (status === 2) {
      const details = jwtDecode(payload.access_token)
      setMe({ status: 0, payload: null })
      setGoogleConvert({ status: 0, payload: null })

      if (
        typeof details.is_active !== 'undefined' &&
        details.is_active === false
      ) {
        setAccountInactive(true)
        logout()
      } else {
        if (
          typeof details.is_verified_email !== 'undefined' &&
          details.is_verified_email === true
        ) {
          redirectLogin(details)
          setLoginStateV2({ status: 0, payload: null })
        } else {
          details.tokenType = payload.token_type
          details.accessToken = payload.access_token
          setSocialMediaToVerify(details)
        }
      }
    } else if (status === 3) {
      if (
        typeof payload.error !== 'undefined' &&
        payload.error === AUTH_FLAGS.ACCOUNT_PERMISSION_REQUIRED
      ) {
        setAccSharingModalVisible(true)
      } else {
        setGoogleConvert({ status: 0, payload: null })
        errorMessage('Google login failed.')
      }
    }
  }, [convertedGoogleToken])

  useEffect(() => {
    const { status, payload } = convertedFacebookToken
    if (status === 2) {
      const details = jwtDecode(payload.access_token)
      setMe({ status: 0, payload: null })
      setFacebookConvert({ status: 0, payload: null })

      if (
        typeof details.is_active !== 'undefined' &&
        details.is_active === false
      ) {
        setAccountInactive(true)
        logout()
      } else {
        if (
          typeof details.is_verified_email !== 'undefined' &&
          details.is_verified_email === true
        ) {
          redirectLogin(details)
          setLoginStateV2({ status: 0, payload: null })
        } else {
          details.tokenType = payload.token_type
          details.accessToken = payload.access_token
          setSocialMediaToVerify(details)
        }
      }
    } else if (status === 3) {
      if (
        typeof payload.error !== 'undefined' &&
        payload.error === AUTH_FLAGS.ACCOUNT_PERMISSION_REQUIRED
      ) {
        setAccSharingModalVisible(true)
      } else {
        errorMessage('Facebook login failed.')
        setFacebookConvert({ status: 0, payload: null })
      }
    }
  }, [convertedFacebookToken])

  useEffect(() => {
    const { status, payload } = me
    if (status === 2 && payload.details !== null) {
      const { details } = payload
      if (details.email && details.is_verified_email) {
        if (!accSharingModalVisible) {
          redirectLogin(details)
          setMe({ status: 0, payload: null })
        }
      } else if (!details.mobile && !details.is_mobile_verified) {
        setSocialMediaToVerify({
          first_name: details.first_name,
          last_name: details.last_name,
          mobile: details.mobile,
          email: details.email
        })
        router.push('/auth/register')
      }
    } else if (status === 3) {
      errorMessage(GENERIC_FAIL_MESSAGE)
      setMe({ status: 0, payload: null })
    }
  }, [me])

  useEffect(() => {
    if (socialMediaToVerify && socialMediaToVerify.accessToken) {
      router.push('/auth/register')
    }
  }, [socialMediaToVerify])

  const redirectLogin = (details) => {
    const cachedSelection = JSON.parse(localStorage.getItem('selected_options'))
    // getMe()
    const {
      first_name: firstName,
      last_name: lastName,
      mobile,
      email
    } = details

    const formattedMobile = mobile?.startsWith('0')
      ? `+63${mobile.slice(1)}`
      : mobile

    const profile = {
      Site: {
        Name: String(`${firstName || ''} ${lastName || ''}`),
        Identity: formattedMobile,
        Email: email,
        Phone: formattedMobile,
        first_name: firstName,
        last_name: lastName
      }
    }

    pushProfile(profile)

    if (latestActiveSc.status === 0) {
      getLatestActiveSc({ Channel: SC_ORIGIN })
      getScCardsV2({ Channel: SC_ORIGIN })
    }
    successMessage('Login Successfully!')
    setAccSharingModalVisible(false)
    sessionStorage.removeItem('creds')
    sessionStorage.removeItem('socialLogin')

    // Determine redirect URL based on cached selection
    const redirectUrl = cachedSelection
      ? `/catalog/${cachedSelection.product.product_data.id}`
      : '/'

    serviceWorker(redirectUrl)
    requestNotificationPermission()
    router.push(redirectUrl)
  }

  return children({
    handleLogin,
    form,
    loginStateV2,
    handleLoginFacebook,
    handleLoginGoogle,
    agreeToShare,
    accSharingModalVisible,
    checkIfPasswordIsUpdated,
    cancelCurrentLogin,
    verifyEmailState,
    forceResetOtpState,
    passwordStatus,
    accountInactive,
    setAccountInactive,
    confirmOtp,
    setConfirmOtp,
    setPasswordStatus,
    passwordUpdatedModalVisible,
    setPasswordUpdatedModalVisible,
    handleClosePasswordUpdatedModal
  })
}
